import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Layout from './Layout';

const FallbackRoute = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);

  // Enhanced route mappings for broken/old URLs
  const routeMappings = useMemo(() => ({
    // Legacy route mappings
    '/schedules': '/evaluations/scheduling',
    '/api-config': '/evaluations/api-config',
    '/webhooks': '/evaluations/webhooks',
    '/results': '/evaluations/results',
    '/comparison': '/model-comparisons',
    '/admin/templates': '/admin',
    '/admin/analytics': '/admin',

    // Common typos and variations
    '/dashbaord': '/dashboard',
    '/dashbord': '/dashboard',
    '/dasboard': '/dashboard',
    '/profil': '/profile',
    '/profile/organisations': '/profile/organization',
    '/organisation-settings': '/organization-settings',
    '/llm-setting': '/llm-settings',
    '/website-check': '/website-checks',
    '/evaluation': '/evaluations',
    '/template': '/templates',
    '/team-invitation': '/team-invitations',

    // Blog variations
    '/blogs': '/blog',
    '/posts': '/blog',
    '/articles': '/blog',
    '/news': '/blog',

    // Product variations
    '/features': '/product/features',
    '/pricing': '/product/pricing',
    '/docs': '/product/documentation',
    '/documentation': '/product/documentation',
    '/api': '/product/api',

    // Company variations
    '/about': '/company/about',
    '/contact': '/company/contact',
    '/careers': '/company/careers',
    '/jobs': '/company/careers',

    // Legal variations
    '/privacy': '/legal/privacy',
    '/terms': '/legal/terms',
    '/security': '/legal/security',
    '/privacy-policy': '/legal/privacy',
    '/terms-of-service': '/legal/terms',
    '/terms-and-conditions': '/legal/terms',

    // Authentication variations
    '/signin': '/login',
    '/sign-in': '/login',
    '/signup': '/register',
    '/sign-up': '/register',
    '/register': '/register',

    // Common path variations
    '/home': '/',
    '/index': '/',
    '/main': '/',
  }), []);

  // All valid routes for suggestion matching
  const validRoutes = useMemo(() => [
    { path: '/', name: 'Home', category: 'Main' },
    { path: '/dashboard', name: 'Dashboard', category: 'Main' },
    { path: '/profile', name: 'Profile', category: 'Account' },
    { path: '/organization-settings', name: 'Organization Settings', category: 'Account' },
    { path: '/change-password', name: 'Change Password', category: 'Account' },
    { path: '/website-checks', name: 'Website Checks', category: 'Tools' },
    { path: '/llm-settings', name: 'LLM Settings', category: 'Tools' },
    { path: '/evaluations', name: 'Evaluations', category: 'Evaluations' },
    { path: '/evaluations/api-config', name: 'API Configuration', category: 'Evaluations' },
    { path: '/evaluations/scheduling', name: 'Scheduling', category: 'Evaluations' },
    { path: '/evaluations/webhooks', name: 'Webhooks', category: 'Evaluations' },
    { path: '/evaluations/results', name: 'Results', category: 'Evaluations' },
    { path: '/templates', name: 'Templates', category: 'Templates' },
    { path: '/team-invitations', name: 'Team Invitations', category: 'Team' },
    { path: '/blog', name: 'Blog', category: 'Content' },
    { path: '/product/features', name: 'Features', category: 'Product' },
    { path: '/product/pricing', name: 'Pricing', category: 'Product' },
    { path: '/product/documentation', name: 'Documentation', category: 'Product' },
    { path: '/company/about', name: 'About', category: 'Company' },
    { path: '/company/contact', name: 'Contact', category: 'Company' },
    { path: '/admin', name: 'Admin Dashboard', category: 'Admin' },
  ], []);

  const levenshteinDistance = useCallback((str1, str2) => {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  }, []);

  const calculateSimilarity = useCallback((str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    if (longer.includes(shorter) || shorter.includes(longer)) {
      return 0.8;
    }

    const editDistance = levenshteinDistance(str1, str2);
    return (longer.length - editDistance) / longer.length;
  }, [levenshteinDistance]);

  const generateSuggestions = useCallback((currentPath) => {
    const suggestions = [];

    validRoutes.forEach(route => {
      const similarity = calculateSimilarity(currentPath.toLowerCase(), route.path.toLowerCase());
      if (similarity > 0.3) {
        suggestions.push({
          ...route,
          similarity
        });
      }
    });

    return suggestions
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 5);
  }, [calculateSimilarity, validRoutes]);

  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();

    const mappingKey = Object.keys(routeMappings).find(key =>
      key.toLowerCase() === currentPath
    );

    if (mappingKey) {
      navigate(routeMappings[mappingKey], { replace: true });
      return;
    }

    const partialMatch = Object.keys(routeMappings).find(key => {
      const keyBase = key.split('/')[1];
      const pathBase = currentPath.split('/')[1];
      return keyBase && pathBase && keyBase.includes(pathBase) && pathBase.length > 2;
    });

    if (partialMatch) {
      const targetBase = routeMappings[partialMatch];
      const pathSuffix = currentPath.substring(currentPath.indexOf('/', 1));
      navigate(targetBase + pathSuffix, { replace: true });
      return;
    }

    const pathSuggestions = generateSuggestions(currentPath);
    setSuggestions(pathSuggestions);

    if (process.env.NODE_ENV === 'production') {
      logPageNotFound(currentPath);
    }
  }, [location.pathname, navigate, generateSuggestions, routeMappings]);

  const logPageNotFound = async (path) => {
    try {
      await fetch('/api/analytics/404', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path,
          referrer: document.referrer,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.debug('Failed to log 404:', error);
    }
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h1 className="text-9xl font-bold text-gray-300">404</h1>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Page not found
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              The page <code className="px-2 py-1 bg-gray-100 rounded text-xs">{location.pathname}</code> could not be found.
            </p>
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Did you mean?
              </h3>
              <div className="space-y-2">
                {suggestions.map((suggestion, index) => (
                  <Link
                    key={index}
                    to={suggestion.path}
                    className="block w-full text-left py-2 px-4 border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {suggestion.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {suggestion.path}
                        </div>
                      </div>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                        {suggestion.category}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-8 space-y-4">
            <button
              onClick={handleGoBack}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go Back
            </button>
            <Link
              to="/"
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go Home
            </Link>
            <Link
              to="/dashboard"
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go to Dashboard
            </Link>
          </div>

          {/* Report broken link */}
          <div className="mt-8 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              Think this should be a valid page?{' '}
              <a
                href={`mailto:support@llmchecks.com?subject=Broken Link Report&body=I found a broken link: ${window.location.href}`}
                className="text-indigo-600 hover:text-indigo-500"
              >
                Report this issue
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FallbackRoute;
